import { Label, PrimaryButton, SearchBox, Spinner, SpinnerSize, Stack, useTheme } from '@fluentui/react';
import { ActivityPage } from 'components';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getAllEncounterReasons, refreshEncounterReasons } from 'state/slices/encounters/encounters.actions';
import { encounterReasonsLoading, encountersReasonsAsList } from 'state/slices/encounters/encounters.selectors';
import { setEncountersSearch } from 'state/slices/encounters/encounters.slice';
import EncounterReasonsList from './components/EncounterReasonsList';
import { completeTasksById, getUserTasksByTarget } from 'state/slices/tasks/actions';
import { UserTaskTarget } from 'state/slices/tasks/model';
import { TaskType, selectActiveAndNewUserTasksByTarget } from 'state/slices/tasks/selectors';
import { LoadingStatus } from 'interfaces/loading-statuses';

export default function EncounterReasons() {
    const dispatch = useDispatch();
    const { palette } = useTheme();
    const { tenantId } = useParams<{
        tenantId: string;
    }>();

    const encountersData = useSelector(encountersReasonsAsList);
    const encountersReasonsLoading = useSelector(encounterReasonsLoading);
    const isEncounterReasonsLoading = encountersReasonsLoading === LoadingStatus.Pending;
    const [search, setSearch] = useState<string | undefined>('');

    const tasks = useSelector(selectActiveAndNewUserTasksByTarget);

    let timer: NodeJS.Timeout | null = null;
    const handleChange = (value: string) => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
            dispatch(setEncountersSearch(value ? value : ''));
        }, 200);
    };

    useEffect(() => {
        dispatch(getUserTasksByTarget({ tenantId, target: UserTaskTarget.TenantSetup }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        dispatch(getAllEncounterReasons({ tenantId }));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantId, dispatch, getAllEncounterReasons]);

    const disableRefreshTask = tasks.find((t) => t.type === TaskType.RefreshingEncounterReason);

    return (
        <ActivityPage
            rightContent={
                <>
                    <PrimaryButton
                        text={disableRefreshTask ? 'Cancel Refresh' : 'Refresh Data'}
                        disabled={isEncounterReasonsLoading && !disableRefreshTask}
                        onClick={() => {
                            if (disableRefreshTask) {
                                dispatch(completeTasksById({ ids: [disableRefreshTask.id] }));
                            } else {
                                dispatch(refreshEncounterReasons({ tenantId }));
                                setSearch('');
                            }
                        }}
                        onRenderText={(props) => {
                            return (
                                <Stack
                                    tokens={{ childrenGap: 10 }}
                                    horizontal
                                >
                                    {(isEncounterReasonsLoading || disableRefreshTask) && <Spinner size={SpinnerSize.medium} />}
                                    <Label styles={{ root: { color: palette.white, '&:hover': { cursor: 'pointer' } }}}>{props?.text ?? ''}</Label>
                                </Stack>
                            );
                        }}
                    />
                    <SearchBox
                        onChange={(_, value) => {
                            const newValue = value ?? '';
                            setSearch(newValue);
                            if (newValue === '') {
                                dispatch(setEncountersSearch(''));
                            } else {
                                handleChange(value ? value : '');
                            }
                        }}
                        onSearch={(value) => dispatch(setEncountersSearch(value ? value : ''))}
                        value={search ? search : ''}
                        style={{ minWidth: 200 }}
                        placeholder="Search"
                    />
                </>
            }
            title="Encounter Reasons"
            hideFooter
            hasPageHeader
        >
            <EncounterReasonsList items={encountersData} loading={encountersReasonsLoading} />
        </ActivityPage>
    );
}
